import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { getShowProductVariantPriceScales } from '@/api-sdk';
import { baseStyles, typographyStyles } from '@/styles';
import { LayoutType, RowData } from '@/types';
import {
  i18nInit,
  makeColumnHeadingsArrayFromQuantityRanges,
  makeColumnRowsArrayFromScales,
  setBaseUrl,
} from '@/utils';
import '../loader/fg-loader';
import './fg-table';

@customElement('fg-product-price-table')
@localized()
export class FgProductPriceTable extends LitElement {
  @property({ type: String })
  product = '';

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'nl_NL';

  /**
   * Debtor is the loggedin reseller ID from CE
   *
   * @property {string} debtor
   */
  @property({ type: String })
  debtor: string | undefined = undefined;

  /**
   * Country is the country from the loggedin reseller from CE
   *
   * @property {string} country
   */
  @property({ type: String })
  country: string | undefined = undefined;

  @property({ type: String })
  layout: LayoutType = 'wholesale';

  @state()
  quantityRanges!: () => string[];

  @state()
  scales: RowData = [];

  @state()
  isLoading = true;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    this.getPriceScaleTable();
  }

  async getPriceScaleTable() {
    try {
      const priceScaleInfo = await getShowProductVariantPriceScales({
        code: this.product,
        params: {
          locale: this.locale,
          debtor: this.debtor,
          country: this.country,
        },
      });

      if (priceScaleInfo) {
        this.isLoading = false;

        this.quantityRanges = makeColumnHeadingsArrayFromQuantityRanges(
          priceScaleInfo.quantityRanges,
        );
        this.scales = makeColumnRowsArrayFromScales(priceScaleInfo.scales);
      }
    } catch {
      this.isLoading = false;
    }
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configuration"></fg-loader>
      `;
    }
    return html`
      <header class="header-price-table">
        <h2>${t('labels.itemPrices')}</h2>
      </header>
      <fg-table
        .tableLabel=${t('labels.variant') || ''}
        .columnHeadings=${this.quantityRanges()}
        .columnRows=${this.scales}
      ></fg-table>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      .header-price-table {
        margin-bottom: 16px;
      }
    `,
  ];
}
